import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  setChartTerm,
  getChartDataRequest,
  getHomeBilateralListRequest,
  getBilateralSAndBListRequest,
  getHomeProductionDataRequest,
  getHomeActiveCertificatesRequest,
  getOrganizedMarketInfoRequest,
} from "../../../redux/actions/homepage";
import {
  convertNumberToFrontend,
  convertAmountToFrontend,
} from "../../../helpers/convert";
import { Bar } from "react-chartjs-2";
import DataTable from "../../../components/DataTable";
import { Select } from "../../../components/Inputs";
import { EmptyState } from "../../../components/EmptyState";
import Loading from "../../../components/Loading";
import Pill from "../../../components/Pill";
import ToggleSwitch from "../../../components/ToggleSwitch";
import { setNotification } from "../../../helpers/notification";
import { numberParse } from "../../../helpers/parsing";
import {
  getSimplifiedSessionsRequested,
  getSessionRequested,
} from "../../../redux/actions/sessions";
import { controlConstants } from "../../../constants/controlConstants";

const Homepage = ({ t, history }) => {
  const dispatch = useDispatch();
  const { yekgLicenseType, organizationId, marketParticipationAgreement } =
    useSelector((state) => state.authorization.user.organization);
  const {
    listBilateral,
    loadingBilateral,
    listBilateralSAndB,
    loadingBilateralSAndB,
  } = useSelector((state) => state.homepage.bilateralAgreement);
  const { year, month, data, loadingChart } = useSelector(
    (state) => state.homepage.chart
  );
  const { listProduction, loadingProduction } = useSelector(
    (state) => state.homepage.productionData
  );
  const { listCertificates, loadingCertificates } = useSelector(
    (state) => state.homepage.activeCertificates
  );
  const { loadingInfo } = useSelector(
    (state) => state.homepage.organizedMarketInfo
  );

  const [filterPeriodOptions, setFilterPeriodOptions] = useState([]);
  const [totalMatchedAmount, setTotalMatchedAmount] = useState(0);

  const translations = {
    BIOMASS: t("ui.default.codes.BIOMASS"),
    WIND: t("ui.default.codes.WIND"),
    HYDROPOWER: t("ui.default.codes.HYDROPOWER"),
    GEOTHERMAL: t("ui.default.codes.GEOTHERMAL"),
    SOLAR: t("ui.default.codes.SOLAR"),
  };

  const resourceTypes = [
    { name: translations.BIOMASS, value: "BIOMASS" },
    { name: translations.WIND, value: "WIND" },
    { name: translations.HYDROPOWER, value: "HYDROPOWER" },
    { name: translations.GEOTHERMAL, value: "GEOTHERMAL" },
    { name: translations.SOLAR, value: "SOLAR" },
  ];

  const sessions = useSelector((state) => state.sessions.list);

  const currentContracts = useSelector(
    (state) => state.sessions.selectedSessionContracts
  );

  const organizedMarketColumns = [
    {
      name: t("pages.default.homepage.table.contract"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.averagePrice"),
      selector: "averagePrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({
          num: row.averagePrice || 0,
          digit: 2,
        });
      },
    },
    {
      name: t("pages.default.homepage.table.matchAmount"),
      selector: "totalAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.totalAmount);
      },
    },
  ];

  const pendingApprovalBilateralAgreementsColumns = [
    {
      name: t("pages.default.bilateralAgreements.table.seller"),
      selector: "fromOrganizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.bilateralAgreements.table.buyer"),
      selector: "toOrganizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.bilateralAgreements.table.status"),
      selector: "status",
      sortable: false,
      cell: (row) => {
        switch (row.status) {
          case "INITIAL":
            if (row.fromOrganizationId === organizationId) {
              return (
                <Pill
                  type="attention"
                  label={t(
                    "pages.default.bilateralAgreements.status.pendingApproval"
                  )}
                />
              );
            }
            return (
              <Pill
                type="important"
                label={t(
                  "pages.default.bilateralAgreements.status.waitingForYourApproval"
                )}
              />
            );
          case "AGREEMENT":
            return (
              <Pill
                type="success"
                label={t(
                  "pages.default.bilateralAgreements.status.bilateralConfirmationReceived"
                )}
              />
            );
          default:
            return;
        }
      },
    },
  ];

  useEffect(() => {
    dispatch(getHomeBilateralListRequest(organizationId));
    dispatch(getBilateralSAndBListRequest(organizationId));
    yekgLicenseType === controlConstants?.PRODUCTION &&
      dispatch(getHomeProductionDataRequest(organizationId));
    dispatch(getHomeActiveCertificatesRequest(organizationId));
    dispatch(getOrganizedMarketInfoRequest());
    dispatch(getSimplifiedSessionsRequested());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getChartDataRequest(year, month));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => {
    if (currentContracts) {
      const totalAmount = currentContracts.reduce(
        (acc, current) => (acc += current.totalAmount ?? 0),
        0
      );
      setTotalMatchedAmount(totalAmount);
    }
  }, [currentContracts]);

  useEffect(() => {
    if (null != sessions && sessions.length > 0) {
      dispatch(getSessionRequested({ sessionId: sessions[0].value }));
    }
  }, [sessions]);

  useEffect(() => {
    // set Period options
    let d = new Date();
    let filterPeriods = [];
    for (let i = 0; i <= 11; i++) {
      const name =
        t(`ui.default.months.${moment().subtract(i, "month").format("M")}`) +
        " " +
        moment().subtract(i, "month").format("YYYY");
      filterPeriods.push({
        name,
        value: moment().subtract(i, "month").format("YYYY-M"),
      });
      d.setMonth(d.getMonth() - 1);
    }
    setFilterPeriodOptions(filterPeriods);
  }, [t]);

  const createCertifiableProductionColumns = () => {
    let columns = [];
    for (let i = 3; i >= 1; i--) {
      const monthValue = moment().subtract(i, "month").format("M");
      columns.push({
        name: t(`ui.default.months.${monthValue}`),
        selector: monthValue,
        cell: (row) =>
          row[`amount${monthValue}`]
            ? numberParse(row[`amount${monthValue}`], 0)
            : "-",
      });
    }
    return columns;
  };

  const createActiveDocumentsColumns = () => {
    return resourceTypes.map((type) => ({
      name: type.name,
      selector: type.value,
      cell: (row) =>
        convertAmountToFrontend(row[type.value])
          ? convertAmountToFrontend(row[type.value])
          : "-",
    }));
  };

  const createBilateralAgreementSAndB = () => {
    const columns = [
      {
        name: t("pages.default.homepage.table.processTypeDate"),
        selector: "processType",
        sortable: true,
        cell: (row) => (
          <div
            className={
              row.processType === "ALIS"
                ? "km__u--text-red-300"
                : "km__u--text-green-300"
            }
          >
            {t(`pages.default.homepage.table.${row.processType}`)}
          </div>
        ),
      },
    ];
    for (let i = 3; i >= 1; i--) {
      const date = moment().subtract(i, "days");
      columns.push({
        name: date.format("DD.MM.YYYY"),
        selector: `amount${date.format("D")}`,
        sortable: true,
        cell: (row) =>
          convertAmountToFrontend(row[`amount${date.format("D")}`]),
      });
    }
    return columns;
  };

  const handleChartTermChange = (e) => {
    dispatch(setChartTerm(e.target.value));
  };

  const handlePrepareChartData = () => {
    const chartDataLabels = {
      exportCount: t("pages.default.homepage.tabs.export").toUpperCase(),
      redemptionCount: t("ui.default.codes.REDEMPTION").toUpperCase(),
      cancelCount: t("ui.default.codes.CANCEL").toUpperCase(),
      repealCount: t("ui.default.codes.REPEAL").toUpperCase(),
    };
    let labels = [];
    let dataSet = [];
    for (let key in data) {
      labels.push(chartDataLabels[key]);
      dataSet.push(data[key]);
    }
    return {
      labels,
      datasets: [
        {
          label: t("pages.default.homepage.chart.documentAmount"),
          data: dataSet,
          backgroundColor: "#2684ff",
        },
      ],
    };
  };
  const handleToggleRedirection = () => {
    marketParticipationAgreement
      ? history.push("offer-operations")
      : setNotification(
          "error",
          "Organize YEK-G piyasa katılım anlaşması imzalamadığınız için organize piyasa ekranlarına erişeminiz bulunmamaktadır."
        );
  };
  const filterSession = (e) => {
    dispatch(getSessionRequested({ sessionId: e.target.value }));
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis km__p--homepage-header-container">
        {t("pages.default.homepage.title")}
        <div className="km__p--homepage-switch-container">
          <ToggleSwitch
            label={t("pages.default.homepage.organizedMarket")}
            checked={false}
            onClick={handleToggleRedirection}
            readOnly
            id="home-toggle"
          />
        </div>
      </h5>

      <div className="km__p--homepage-row-container">
        <div>
          {yekgLicenseType === controlConstants?.PRODUCTION ||
            (yekgLicenseType === controlConstants?.AGGREGATOR && (
              <div className="km__p--card km__p--card-home">
                <div className="km__p--homepage-header">
                  <div className="km__u--text_title">
                    {t("pages.default.homepage.certifiableProductionAmount")}
                  </div>
                </div>
                {!loadingProduction && (
                  <DataTable
                    columns={createCertifiableProductionColumns()}
                    data={listProduction}
                    rowKey="id"
                  />
                )}
                {loadingProduction && <Loading />}
              </div>
            ))}

          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header">
              <div className="km__u--text_title">
                {t("pages.default.homepage.activeDocuments")}
              </div>
            </div>
            {!loadingCertificates && (
              <DataTable
                columns={createActiveDocumentsColumns()}
                data={listCertificates}
                rowKey="id"
              />
            )}
            {loadingCertificates && <Loading />}
          </div>

          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header">
              <div className="km__u--text_title">
                {t("pages.default.homepage.bilateralAgreementSAndB")}
              </div>
            </div>
            {!loadingBilateralSAndB && (
              <DataTable
                columns={createBilateralAgreementSAndB()}
                data={listBilateralSAndB}
                rowKey="processType"
              />
            )}
            {loadingBilateralSAndB && <Loading />}
          </div>

          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header">
              <div className="km__u--text_title">
                {t("pages.default.homepage.pendingApprovalBilateralAgreements")}
              </div>
            </div>
            {!loadingBilateral && (
              <DataTable
                columns={pendingApprovalBilateralAgreementsColumns}
                data={listBilateral}
                rowKey="id"
              />
            )}
            {!loadingBilateral && listBilateral.length === 0 && (
              <EmptyState
                icon="file-text"
                text={t("pages.default.bilateralAgreements.activeNotFound")}
              />
            )}
            {loadingBilateral && (
              <Loading
                text={t("pages.default.bilateralAgreements.activeLoading")}
              />
            )}
          </div>
        </div>

        <div>
          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header km__u--text_title">
              <div>
                {t("pages.default.homepage.organizedMarketInformation")}
              </div>
              {sessions && (
                <Select
                  options={sessions}
                  name="sessionDate"
                  defaultValue={sessions[sessions.length - 1]}
                  onChange={filterSession}
                />
              )}
            </div>
            {!loadingInfo && (
              <DataTable
                columns={organizedMarketColumns}
                data={currentContracts || []}
                rowKey="id"
              />
            )}
            {loadingInfo && <Loading />}
            <div className="km__p--homepage-total-container km__u--text_title">
              {t("pages.default.homepage.totalMatchAmount")}:{" "}
              {convertAmountToFrontend(totalMatchedAmount)}
            </div>
          </div>

          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header">
              <div className="km__u--text_title">
                {t("pages.default.homepage.periodicGeneralOperations")}
              </div>
              <Select
                options={filterPeriodOptions}
                name="period"
                defaultValue={`${year}-${month}`}
                onChange={handleChartTermChange}
                id="home-select-period"
              />
            </div>
            {!loadingChart && (
              <div className={"chart-container"}>
                <Bar
                  data={handlePrepareChartData}
                  options={{
                    maintainAspectRatio: false,
                    type: "bar",
                    scales: {
                      yAxes: [
                        {
                          stacked: true,
                          // id: "first-y-axis",
                          // position: "left",
                          // type: "logarithmic",
                          ticks: {
                            suggestedMin: 1,
                            suggestedMax: 20,
                            // callback: function (value, index, values) {
                            //   if (value === 1000000) return "1M";
                            //   if (value === 100000) return "100K";
                            //   if (value === 10000) return "10K";
                            //   if (value === 1000) return "1K";
                            //   if (value === 100) return "100";
                            //   if (value === 10) return "10";
                            //   if (value === 0) return "0";
                            //   return null;
                            // },
                          },
                        },
                      ],
                      xAxes: [
                        {
                          barThickness: 20,
                          maxBarThickness: 20,
                          stacked: true,
                        },
                      ],
                    },
                  }}
                />
              </div>
            )}
            {loadingChart && <Loading />}
          </div>
        </div>
      </div>
    </>
  );
};

export default Homepage;
